// Puerto de la liquidacion con montos del proveedor (LQ-4, ADR-026 §6 y
// ADR-025). La vista habla con esto y no con fetch: en los tests se reemplaza
// por un doble y en la app por settlement-http.ts.
//
// Sin planta ni proveedor en el resumen: el conjunto de plantas lo resuelve
// la API en cada request desde la habilitacion activa de la empresa madre. El
// borrador por planta recibe solo la planta; el proveedor sale de la
// habilitacion. La API audita cada lectura con montos.
import type { CreateSettlementDisputeRequest, ProviderSettlementsResponse, SettlementDraft, SettlementReview } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import type { TripExportFile } from "./port.js";

export interface SettlementPort {
  /** Una fila por planta habilitada: totales si el par esta en full, o la modalidad export_only. Sin fecha, hoy en cada planta. */
  summary(session: PortalSession, date?: string): Promise<ProviderSettlementsResponse>;
  /** Borrador de la empresa en la planta, en el periodo que contiene `date`. */
  draft(session: PortalSession, siteId: string, date: string): Promise<SettlementDraft>;
  /** El mismo borrador en archivo, con montos. */
  download(session: PortalSession, siteId: string, date: string, format: "csv" | "xlsx"): Promise<TripExportFile>;
  /** LQ-6: conformidad de la empresa con el borrador vigente (su hash). Responde sin montos. */
  approve(session: PortalSession, siteId: string, periodId: string, inputsHash: string): Promise<SettlementReview>;
  /** LQ-6: observacion sobre un viaje o una linea del periodo en revision. Responde sin montos. */
  dispute(session: PortalSession, siteId: string, periodId: string, request: CreateSettlementDisputeRequest): Promise<SettlementReview>;
}

/**
 * Por que fallo una llamada, sin el cuerpo remoto: la UI elige el copy por
 * este codigo y nunca pinta el `detail` de la API.
 * - access: 403 (la empresa no tiene habilitacion activa en la planta).
 * - session: 401 (sesion vencida o rechazada).
 * - validation: 400 (fecha que la API no acepta).
 * - not_full: 409 settlement_not_full (el par liquida con el exportable sin montos).
 * - period_state: 409 settlement_period_state (el periodo ya no esta en revision).
 * - draft_changed: 409 settlement_draft_changed (el borrador cambio: volver a mirarlo).
 * - unavailable: 404, 5xx, red o respuesta que viola el contrato.
 */
export type SettlementErrorKind = "access" | "session" | "validation" | "not_full" | "period_state" | "draft_changed" | "unavailable";

export class SettlementError extends Error {
  constructor(readonly kind: SettlementErrorKind) {
    super(`settlement_${kind}`);
  }
}
